<template>
  <div class="subsidiary-cards">
    <!-- Toolbar -->
    <table-toolbar
      v-if="showToolbar"
      :show-title="showHeader"
      :title="title"
      :show-count="true"
      :current-count="subsidiaries.filtered.length"
      :total-count="subsidiaries.all.length"
      :searchable="showSearch"
      :search-query="searchQuery"
      @search-change="searchQuery = $event"
      :show-per-page-selector="false"
      :show-refresh="false"
      :show-column-visibility="showColumnVisibility && viewMode === 'list'"
      :columns="availableTableColumns"
      :visible-columns="visibleColumns"
      @column-toggle="handleColumnVisibilityChange"
      :show-view-toggle="showViewToggle"
      :view-mode="viewMode"
      @view-mode-change="viewMode = $event"
      :disabled="loading"
    >
      <template #action-buttons>
        <slot name="action-buttons"></slot>
        <themed-button
          @click="$modal.show('create-subsidiary')"
          variant="default"
        >
          <theme-icon icon="edit-icon" type="icons" classes="w-4 h-4" />
          <span>Manage Subsidiaries</span>
        </themed-button>
      </template>
    </table-toolbar>

    <!-- Grid View -->
    <themed-grid
      v-if="viewMode === 'grid'"
      :data="filteredSubsidiaries"
      :fields="gridFields"
      :columns="3"
      :clickable="false"
      :loading="loading"
      empty-message="No subsidiaries found"
      empty-icon="building-icon"
      item-key="id"
    >
      <template #card-actions="{ item }">
        <div class="subsidiary-cards__card-actions">
          <button
            @click="showEditSubsidiary(item.id)"
            class="subsidiary-cards__card-action"
            title="Edit Subsidiary"
          >
            <theme-icon icon="edit-icon" type="icons" classes="w-4 h-4" />
          </button>
          <button
            @click="confirmDeleteSubsidiary(item.id)"
            class="subsidiary-cards__card-action subsidiary-cards__card-action--danger"
            title="Delete Subsidiary"
          >
            <theme-icon icon="trash-icon" type="icons" classes="w-4 h-4" />
          </button>
        </div>
      </template>
    </themed-grid>

    <!-- List View -->
    <themed-table
      v-else
      :data="filteredSubsidiaries"
      :columns="visibleTableColumns"
      :hoverable="true"
      :searchable="false"
      :column-visibility="false"
      empty-message="No subsidiaries found"
      empty-icon="building-icon"
      @action="handleAction"
    >
      <template #column-actions="{ row }">
        <div class="flex items-center justify-end gap-2">
          <themed-button
            size="sm"
            variant="ghost"
            @click="showEditSubsidiary(row.id)"
          >
            <theme-icon icon="edit-icon" type="icons" classes="w-4 h-4" />
          </themed-button>
          <themed-button
            size="sm"
            variant="ghost"
            @click="confirmDeleteSubsidiary(row.id)"
          >
            <theme-icon icon="trash-icon" type="icons" classes="w-4 h-4" />
          </themed-button>
        </div>
      </template>
    </themed-table>

    <!-- Create/Edit Subsidiary Modal -->
    <create-subsidiary
      :client-id="forClientId"
      :subsidiary-id="selectedSubsidiaryId"
      @subsidiaryCreated="handleSubsidiaryCreated"
      @subsidiaryUpdated="handleSubsidiaryUpdated"
      @closed="selectedSubsidiaryId = null"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import ThemedTable from '../ThemedTable.vue';
import ThemedGrid from '../ThemedGrid.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemeIcon from '../ThemeIcon.vue';
import TableToolbar from '../table/TableToolbar.vue';
import CreateSubsidiary from './CreateSubsidiary.vue';

export default {
  name: 'SubsidiaryCards',

  components: {
    ThemedTable,
    ThemedGrid,
    ThemedButton,
    ThemeIcon,
    TableToolbar,
    CreateSubsidiary
  },

  props: {
    subsidiaryData: {
      type: Array,
      required: true
    },

    forClientId: {
      type: Number,
      required: true
    },

    showHeader: {
      type: Boolean,
      default: true
    },

    smallHeader: {
      type: Boolean,
      default: false
    },

    showFilters: {
      type: Boolean,
      default: true
    },

    showToolbar: {
      type: Boolean,
      default: true
    },

    showSearch: {
      type: Boolean,
      default: true
    },

    showViewToggle: {
      type: Boolean,
      default: true
    },

    view: {
      type: String,
      default: 'grid'
    },

    title: {
      type: String,
      default: 'Subsidiaries'
    },

    showColumnVisibility: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      selectedSubsidiaryId: null,
      viewMode: this.view,
      searchQuery: '',
      loading: false,
      visibleColumns: []
    };
  },

  computed: {
    ...mapState(['subsidiaries']),

    filteredSubsidiaries() {
      let filtered = this.subsidiaries.filtered;

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(subsidiary => {
          const name = subsidiary.name || '';
          const address = subsidiary.address || '';
          const city = subsidiary.city || '';
          return name.toLowerCase().includes(query) ||
                 address.toLowerCase().includes(query) ||
                 city.toLowerCase().includes(query);
        });
      }

      return filtered;
    },

    gridFields() {
      return [
        {
          field: 'name',
          type: 'title'
        },
        {
          field: 'address',
          label: 'ADDRESS',
          type: 'text',
          transform: (value) => value ? (value.length > 20 ? value.substring(0, 20) + '...' : value) : ''
        },
        {
          field: 'city',
          label: 'CITY',
          type: 'text'
        },
        {
          field: 'state',
          label: 'STATE',
          type: 'text'
        },
        {
          field: 'postal_code',
          label: 'ZIP',
          type: 'text'
        },
        {
          field: 'country',
          label: 'COUNTRY',
          type: 'text',
          transform: (value) => value?.name
        }
      ];
    },

    tableColumns() {
      return [
        {
          field: 'name',
          label: 'Name',
          type: 'text',
          sortable: true,
          showInTable: true,
          alwaysVisible: true
        },
        {
          field: 'address',
          label: 'Address',
          type: 'text',
          sortable: true,
          showInTable: true
        },
        {
          field: 'city',
          label: 'City',
          type: 'text',
          sortable: true,
          showInTable: true
        },
        {
          field: 'state',
          label: 'State',
          type: 'text',
          width: '80px',
          sortable: true,
          showInTable: true
        },
        {
          field: 'postal_code',
          label: 'Zip',
          type: 'text',
          width: '100px',
          sortable: true,
          showInTable: true
        },
        {
          field: 'country',
          label: 'Country',
          type: 'text',
          sortable: true,
          showInTable: false,
          transform: (value) => value?.name
        },
        {
          field: 'actions',
          label: '',
          type: 'actions',
          width: '120px',
          align: 'right',
          showInTable: true,
          alwaysVisible: true,
          actions: [
            {
              label: 'Edit',
              variant: 'default',
              handler: (row) => this.showEditSubsidiary(row.id)
            },
            {
              label: 'Delete',
              variant: 'destructive',
              handler: (row) => this.confirmDeleteSubsidiary(row.id)
            }
          ]
        }
      ];
    },

    availableTableColumns() {
      return this.tableColumns.filter(col => col.showInTable !== false);
    },

    visibleTableColumns() {
      return this.tableColumns.filter(col => {
        if (col.alwaysVisible) return true;
        return this.visibleColumns.includes(col.field);
      });
    }
  },

  watch: {
    subsidiaryData: {
      immediate: true,
      handler(newData) {
        if (newData && newData.length > 0) {
          this.subsidiaries.all = newData;
          this.subsidiaries.filtered = newData;
          this.$emit('filterRequest');
        }
      }
    },

    viewMode(newValue) {
      localStorage.setItem('reopt_subsidiaries_layout', newValue);
    }
  },

  mounted() {
    if (localStorage.getItem('reopt_subsidiaries_layout')) {
      this.viewMode = localStorage.getItem('reopt_subsidiaries_layout');
    }

    this.$eventHub.$on('updateSubsidiaries', () => {
      this.$store.dispatch('loadSubsidiaries', this.forClientId);
    });

    // Initialize visible columns
    const savedColumns = localStorage.getItem('subsidiaries_table_visible_columns');
    if (savedColumns) {
      try {
        this.visibleColumns = JSON.parse(savedColumns);
      } catch (e) {
        this.initializeDefaultColumns();
      }
    } else {
      this.initializeDefaultColumns();
    }
  },

  methods: {
    initializeDefaultColumns() {
      this.visibleColumns = this.tableColumns
        .filter(col => col.showInTable !== false && !col.alwaysVisible)
        .map(col => col.field);
    },

    handleColumnVisibilityChange(columns) {
      this.visibleColumns = columns;
      localStorage.setItem('subsidiaries_table_visible_columns', JSON.stringify(columns));
    },

    showEditSubsidiary(subsidiaryId) {
      this.selectedSubsidiaryId = subsidiaryId;
      this.$modal.show('create-subsidiary');
    },

    confirmDeleteSubsidiary(subsidiaryId) {
      this.$swal({
        title: 'Are you sure?',
        text: 'Are you sure you want to delete this subsidiary?',
        type: 'warning',
        buttonsStyling: false,
        confirmButtonText: 'Yes - Delete it',
        confirmButtonClass: 'btn btn-red',
        showCancelButton: true,
        cancelButtonClass: 'btn btn-secondary mr-4',
        reverseButtons: true
      }).then((result) => {
        if (result.value) {
          this.deleteSubsidiary(subsidiaryId);
        }
      });
    },

    async deleteSubsidiary(subsidiaryId) {
      if (subsidiaryId == 'new') {
        _.remove(this.subsidiaries.all, (row) => row.id == 'new');
        return;
      }

      try {
        const response = await axios.delete('/subsidiaries/' + subsidiaryId);

        this.$notify({
          type: 'success',
          title: 'Success',
          text: response.data.message
        });

        this.$eventHub.$emit('reloadClient');
      } catch (error) {
        this.$notify({
          type: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Failed to delete subsidiary'
        });
      }
    },

    handleAction(action, row) {
      if (action === 'edit') {
        this.showEditSubsidiary(row.id);
      } else if (action === 'delete') {
        this.confirmDeleteSubsidiary(row.id);
      }
    },

    handleSubsidiaryCreated(subsidiary) {
      this.$eventHub.$emit('reloadClient');
    },

    handleSubsidiaryUpdated(subsidiary) {
      this.$eventHub.$emit('reloadClient');
    }
  }
};
</script>
